import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, CheckCircle, Clock, Award, Phone, Home as HomeIcon, Building2, Handshake, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo, testimonials } from '../mockData';
import { siteConfig, fixedQuoteLine, rates } from '../content/siteContent';
import usePageMeta from '../hooks/usePageMeta';
import TrustStrip from '../components/TrustStrip';

const audiences = [
  {
    icon: HomeIcon,
    title: 'Residential',
    text: 'Lockouts, rekeying, key cutting, lock repairs and home security upgrades — mobile across the North Shore.',
    to: '/residential',
    cta: 'Residential services',
  },
  {
    icon: Building2,
    title: 'Commercial',
    text: 'Door hardware, access control installation, maintenance and key systems for businesses, property and facilities managers.',
    to: '/commercial',
    cta: 'Commercial services',
  },
  {
    icon: Handshake,
    title: 'Trade partners',
    text: 'The installation delivery partner access control companies and builders rely on across Auckland.',
    to: '/partners',
    cta: 'Partner with us',
  },
];

const Home = () => {
  usePageMeta({
    title: 'Shore Doors Locksmiths | Mobile Locksmiths & Door Hardware — North Shore, Auckland',
    description:
      "The lock and door hardware specialists Auckland's security companies already use — now serving homes and businesses directly. Mobile service, fixed quotes, 12-month workmanship guarantee.",
  });

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-stone-100 via-white to-red-50 py-16 sm:py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield size={16} />
                Mobile locksmiths — we come to you
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                The lock specialists the professionals use
                <span className="block text-blue-600">now for your home & business</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0">
                Shore Doors has installed commercial locks and door hardware across Auckland's
                hospitals, banks and public buildings for years — as the trusted install partner
                of security companies. Now that expertise is available to everyone, mobile across
                the North Shore and wider Auckland.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a href={`tel:${businessInfo.phoneTel}`}>
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-bold text-lg px-8">
                    <Phone className="mr-2" size={20} />
                    Call {businessInfo.phone}
                  </Button>
                </a>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold text-lg px-8">
                    Get a quote
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-gray-500 mt-5">
                {siteConfig.hoursLine} · {fixedQuoteLine}
              </p>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/stock/cover-lock-wooden-door.jpg"
                  alt="Black mortice lock and keys fitted in a wooden door by Shore Doors Locksmiths"
                  className="w-full h-[400px] sm:h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950/25 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audience cards */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              How can we help?
            </h2>
          </div>
          <div className="max-w-5xl mx-auto mb-10">
            <div className="bg-red-50 border border-red-200 rounded-2xl overflow-hidden flex flex-col sm:flex-row items-stretch">
              <img
                src="/images/work/a200-custom-paint.jpeg"
                alt="AHS A200 automatic swing-door opener installed by Shore Doors Locksmiths"
                className="w-full sm:w-64 h-48 sm:h-auto object-cover flex-shrink-0"
              />
              <div className="p-6 sm:p-8 flex flex-col justify-center">
                <p className="font-bold text-red-700 text-lg mb-1">Auckland's only certified AHS A200 installer</p>
                <p className="text-gray-700 mb-4">
                  Automatic swing-door openers for accessibility — the push-button and hands-free
                  doors we fit regularly for disabled customers through ACC.
                </p>
                <Link to="/commercial/door-automation" className="text-red-700 font-semibold inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">
                  Door automation &amp; accessibility <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {audiences.map((a) => (
              <Card key={a.title} className="border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8 flex flex-col h-full">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-5">
                    <a.icon className="text-blue-600" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{a.title}</h3>
                  <p className="text-gray-600 mb-6 flex-1">{a.text}</p>
                  <Link to={a.to} className="text-blue-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                    {a.cta} <ArrowRight size={16} />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing strip */}
      <section className="py-12 bg-red-600">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <p className="text-white text-2xl font-bold mb-1">
              ${rates.callout} call-out — includes travel and the first 15 minutes
            </p>
            <p className="text-red-100">Then ${rates.block15} per 15 minutes · {fixedQuoteLine}</p>
          </div>
          <Link to="/pricing" className="flex-shrink-0">
            <Button size="lg" className="bg-white text-red-700 hover:bg-red-50 font-bold whitespace-nowrap">
              See full pricing <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Trust strip — badges + brands we install */}
      <TrustStrip />

      {/* Proof strip */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              The installer the professionals already use
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              North Shore Hospital. BNZ's Queen Street head office. Schools, libraries and train
              stations across Auckland. Security companies bring us in because the install has to
              be right.
            </p>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-4 mb-10">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className={`${t.logoBgDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg px-5 py-3 flex items-center justify-center`}
                style={{ minWidth: '150px', height: '60px' }}
              >
                <img src={t.logo} alt={`${t.company} logo`} className="max-h-10 w-auto object-contain" style={{ maxWidth: '130px' }} />
              </div>
            ))}
          </div>
          <div className="max-w-3xl mx-auto">
            <Card className="border-gray-200 bg-white">
              <CardContent className="p-8">
                <Quote className="text-blue-200 mb-3" size={28} />
                <blockquote className="text-gray-700 text-lg leading-relaxed mb-4 italic">
                  "{testimonials[1].quote}"
                </blockquote>
                <p className="font-semibold text-gray-900">{testimonials[1].name}</p>
                <p className="text-gray-500 text-sm">{testimonials[1].title}, {testimonials[1].company}</p>
              </CardContent>
            </Card>
            <div className="text-center mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/reviews">
                <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8">
                  Read all reviews <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
              <Link to="/projects">
                <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8">
                  See completed projects <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Shore Doors Locksmiths?
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="text-red-500" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Commercial-grade expertise</h3>
              <p className="text-gray-300">The same skills trusted on hospitals and banks, applied to your door</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-red-500" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Fixed quotes</h3>
              <p className="text-gray-300">Clear call-out pricing and a fixed quote before any extra work starts</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="text-red-500" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Mobile service</h3>
              <p className="text-gray-300">Fully equipped vans across the North Shore and wider Auckland</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-red-500" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Guaranteed work</h3>
              <p className="text-gray-300">12-month workmanship guarantee, NZ Building Code compliant</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
